import { Chip, Typography, Box, Card, Stack } from "@mui/material";
import {
  spacing,
  colors,
  typography,
} from "../../../../../../../../../assets/styles/tokens";
import type { RevenueRow } from "./PlayerValueTable";
import PlayerValueTable from "./PlayerValueTable";

interface TargetVsActualItem {
  Proportional_Target: number;
  Actual: number;
  Target: number;
  Comment: string;
  Metrics: string;
  RR: number;
  Proportional_Attainment: number;
}

interface PlayerValueProps {
  data: TargetVsActualItem[];
}

const TotalIncomeTableHeadings = {
  metric: "",
  actual_target: "Actual / Target",
  RR: "RR",
  PRR: "PRR",
};

function fmt(value: number): string {
  const rounded = Math.round(value);
  return `$${rounded.toLocaleString()}`;
}

// Positive metrics: Red < 85%, Yellow 85-99%, Green > 99%
function getRrColor(attainment: number): "success" | "warning" | "error" {
  if (attainment > 0.99) return "success"; // Green → > 99%
  if (attainment >= 0.85) return "warning"; // Yellow → 85% to 99%
  return "error"; // Red → < 85%
}

function getRrChipStyles(attainment: number) {
  const color = getRrColor(attainment);
  const map = {
    success: {
      bg: colors.Success[50].$value,
      color: colors.Success[700].$value,
    },
    warning: {
      bg: colors.Warning[50].$value,
      color: colors.Warning[600].$value,
    },
    error: {
      bg: colors.Error[50].$value,
      color: colors.Error[600].$value,
    },
  };
  return map[color];
}

export default function PlayerValue({ data }: PlayerValueProps) {
  const findMetric = (name: string) =>
    data?.find((item) => item.Metrics?.toLowerCase() === name.toLowerCase());

  const pv = findMetric("pv");
  const cpaPv = findMetric("CPA_PV");
  const rsPv = findMetric("RS_PV");

  // Build table rows from API data
  const tableRows: RevenueRow[] = [];
  if (cpaPv) {
    tableRows.push({
      cron_id: "1",
      rrType: getRrColor(cpaPv.Proportional_Attainment),
      metric: "CPA PV",
      actual_target: `${fmt(cpaPv.Actual)} / ${fmt(cpaPv.Target)}`,
      RR: `${(cpaPv.Proportional_Attainment * 100).toFixed(1)}%`,
      PRR: fmt(cpaPv.RR),
    });
  }
  if (rsPv) {
    tableRows.push({
      cron_id: "2",
      rrType: getRrColor(rsPv.Proportional_Attainment),
      metric: "RS PV",
      actual_target: `${fmt(rsPv.Actual)} / ${fmt(rsPv.Target)}`,
      RR: `${(rsPv.Proportional_Attainment * 100).toFixed(1)}%`,
      PRR: fmt(rsPv.RR),
    });
  }

  return (
    <Card
      sx={{
        p: spacing.Spacing.md.$value,
        borderRadius: spacing.Radius.xl.$value,
        boxShadow: "0px 1px 2px #0A0D120F , 0px 1px 3px #0A0D121A",
        border: "1px solid" + colors.GrayLightMode[200].$value,
      }}
    >
      {/* HEADER */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        mb={2}
      >
        <Box>
          <Typography
            sx={{
              fontSize: typography.Size.Text.xs.$value,
              lineHeight: typography.Line_height.text.xs.$value,
              fontWeight: typography.Font_weight.Medium.$value,
              color: colors.GrayLightMode[600].$value,
            }}
          >
            PV
          </Typography>

          <Typography
            sx={{
              fontSize: typography.Size.Text.xl.$value,
              fontWeight: 600,
              color: colors.GrayLightMode[900].$value,
              mt: spacing.Spacing.xs.$value,
            }}
          >
            {pv ? fmt(pv.Actual) : "N/A"}
          </Typography>

          <Typography
            sx={{
              lineHeight: typography.Line_height.text.sm.$value,
              fontSize: typography.Size.Text.sm.$value,
              color: colors.GrayLightMode[600].$value,
              mt: spacing.Spacing.xxs.$value,
            }}
          >
            out of{" "}
            <Box
              component="span"
              sx={{
                lineHeight: typography.Line_height.text.sm.$value,
                fontSize: typography.Size.Text.sm.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
              }}
              color={colors.GrayLightMode[900].$value}
            >
              {pv ? fmt(pv.Target) : "N/A"}
            </Box>
          </Typography>
        </Box>

        <Stack spacing={spacing.Spacing.sm.$value} alignItems="flex-start">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              color={colors.GrayLightMode[700].$value}
              sx={{
                lineHeight: typography.Line_height.text.sm.$value,
                fontSize: typography.Size.Text.sm.$value,
              }}
            >
              RR
            </Typography>
            <Chip
              label={
                pv ? `${(pv.Proportional_Attainment * 100).toFixed(1)}%` : "N/A"
              }
              size="small"
              sx={{
                bgcolor: pv
                  ? getRrChipStyles(pv.Proportional_Attainment).bg
                  : undefined,
                color: pv
                  ? getRrChipStyles(pv.Proportional_Attainment).color
                  : undefined,
                fontSize: typography.Size.Text.sm.$value,
                lineHeight: typography.Line_height.text.sm.$value,
                borderRadius: spacing.Radius.sm.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
                "& .MuiChip-label": {
                  px: spacing.Spacing.md.$value,
                },
              }}
            />
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={"flex-start"}
          >
            <Typography
              color={colors.GrayLightMode[600].$value}
              sx={{
                lineHeight: typography.Line_height.text.sm.$value,
                fontSize: typography.Size.Text.sm.$value,
              }}
            >
              PRR
            </Typography>
            <Typography
              color={colors.GrayLightMode[900].$value}
              sx={{
                lineHeight: typography.Line_height.text.sm.$value,
                fontSize: typography.Size.Text.sm.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
              }}
            >
              {pv ? fmt(pv.RR) : "N/A"}
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {/* TABLE */}
      <PlayerValueTable
        TotalIncomeTableHeadings={TotalIncomeTableHeadings}
        TotalIncomeTableRows={tableRows}
      />
    </Card>
  );
}
